import { Fragment, useEffect, useMemo, useState } from 'react'
import { api, type Constraint, type Guidelines, type Rule } from '../api'
import { FundPicker, useFunds } from '../components/FundPicker'
import { SortHeader, sortRows, useSort } from '../components/Grid'
import { RuleHistory } from '../components/History'
import { NotEvaluated } from '../components/NotEvaluated'
import { AddOnFootnote, EvaluatorName } from '../components/AddOn'
import { Origin } from '../components/Origin'
import { Cited } from '../components/Cited'
import { Explain } from '../components/Explain'
import { RowMenu } from '../components/RowMenu'
import { RetireLimit } from '../components/RetireLimit'
import { layerLabel, says, subjectText } from '../limits'

type Filter = 'all' | 'inherited' | 'own'

/**
 * The lookup dashboard — proposal §D, TECHNICAL_DESIGN.md §5.6.
 *
 * §5.6 requires the dashboard tag each rule with its origin and be able to
 * filter to *inherited only* or *this portfolio only*. That filter is here
 * rather than in the API on purpose: the request always fetches the whole
 * resolved set, so a filtered view is visibly a subset of something, and there
 * is no query the front end can send that returns a portfolio's own rules while
 * looking like it returned everything (§13).
 *
 * **The fund is chosen from a list.** It used to be an empty search box, which
 * asks the reader to already know the answer — over an appliance holding nine
 * funds, *code or name* is a hint about the query language rather than about the
 * data.
 *
 * **The limits are a table.** They were cards, one per `(measure, basis,
 * subject)` group, each holding a small list. That renders one constraint well
 * and forty as a scroll, and the question actually asked here — *what binds this
 * fund* — is a question about the set. A table puts every threshold in one
 * column where they can be read down.
 *
 * What the cards carried and the table has to keep is §6.3's *standing*: of the
 * rules constraining the same thing, which one binds. It is **not** a column,
 * because on the ordinary fund every constraint holds exactly one rule and a
 * column reading `sole` forty times down the page is a column that has told the
 * reader nothing while taking width from what has. It is a badge, on the rows
 * where more than one rule competes — which is where the word means something —
 * and the undetermined case keeps its note, because a silent pick of the first
 * or the tighter-looking number is the manufactured confidence the whole design
 * exists to avoid.
 */

interface Row {
  key: string
  constraint: Constraint
  rule: Rule
  standing: 'binding' | 'overridden' | 'undetermined' | 'sole'
  competing: boolean
}

function flatten(constraints: Constraint[]): Row[] {
  return constraints.flatMap((constraint) => {
    const all = [...(constraint.binding ? [constraint.binding] : []), ...constraint.also]
    return all.map((rule) => ({
      key: `${constraint.measure}|${constraint.basis}|${constraint.subject_key}`,
      constraint,
      rule,
      standing: constraint.undetermined
        ? ('undetermined' as const)
        : all.length === 1
          ? ('sole' as const)
          : constraint.binding && rule.id === constraint.binding.id
            ? ('binding' as const)
            : ('overridden' as const),
      // Whether anything else constrains the same thing. `sole` is the ordinary
      // case and says nothing worth a column.
      competing: all.length > 1 || constraint.undetermined,
    }))
  })
}

function subjectOf(row: Row): string {
  const measure = row.constraint.measure
  if (!measure) return 'narrative'
  return `${measure}${row.constraint.basis ? ` / ${row.constraint.basis}` : ''}`
}

export function Lookup({ canRetire = false }: { canRetire?: boolean } = {}) {
  const { funds, error: fundsError } = useFunds()
  const [fundId, setFundId] = useState('')
  const [guidelines, setGuidelines] = useState<Guidelines | null>(null)
  const [filter, setFilter] = useState<Filter>('all')
  const [pending, setPending] = useState<Rule[]>([])
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)
  // The limit whose retirement is being proposed, and what came of the last one (decision 72).
  const [retiring, setRetiring] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const { sort, onSort } = useSort()

  const selected = funds.find((f) => f.id === fundId) ?? null

  useEffect(() => {
    if (!fundId) {
      setGuidelines(null)
      setPending([])
      return
    }
    let live = true
    setError(null)
    api
      .guidelines(fundId)
      .then((g) => live && setGuidelines(g))
      .catch((e) => live && setError(String(e.message ?? e)))
    // Pending proposals — proposal §D asks for them to be visible, and §8.6
    // means they are not limits. Fetched separately and shown in their own
    // table rather than mixed into the rules: a proposal beside the limits in
    // force, in the same list, reads as one of them however it is labelled.
    api
      .proposals(fundId)
      .then((found) => live && setPending(found))
      .catch((e) => live && setError(String(e.message ?? e)))
    return () => {
      live = false
    }
  }, [fundId])

  const rows = useMemo(() => {
    const kept = (guidelines?.constraints ?? []).filter((c) => {
      if (filter === 'all') return true
      const all = [...(c.binding ? [c.binding] : []), ...c.also]
      return filter === 'inherited'
        ? all.some((r) => r.inherited)
        : all.some((r) => !r.inherited)
    })
    return sortRows(flatten(kept), sort, {
      subject: subjectOf,
      limit: (r) => r.rule.rule_key,
      layer: (r) => layerLabel(r.rule.layer),
      origin: (r) => (r.rule.inherited ? `inherited ${r.rule.origin ?? ''}` : 'own'),
    })
  }, [guidelines, filter, sort])

  const undetermined = rows.some((r) => r.standing === 'undetermined')

  /** How many rules each view holds, so the tabs say what is behind them
   *  rather than making the reader click to find out. */
  const counts = useMemo(() => {
    const all = flatten(guidelines?.constraints ?? [])
    return {
      all: all.length,
      inherited: all.filter((r) => r.rule.inherited).length,
      own: all.filter((r) => !r.rule.inherited).length,
    }
  }, [guidelines])

  return (
    <div className="lookup">
      <header className="page-head">
        <h2>What binds a fund</h2>
        <p className="note">
          Every limit that applies to one portfolio, its own and the ones it
          inherits from a group, with where each came from and which of them
          binds.
        </p>
      </header>

      <div className="filters">
        <FundPicker
          label="Portfolio"
          value={fundId}
          onChange={setFundId}
          funds={funds}
          testId="fund"
        />
      </div>

      {(error || fundsError) && (
        <p className="error" role="alert">
          {error ?? fundsError}
        </p>
      )}
      {notice && <p className="ok">{notice}</p>}

      {!selected ? (
        <p className="empty">Choose a fund to see what binds it.</p>
      ) : (
        guidelines && (
          <section>
            {/* Rendered before the limits, every time. Launch gate 9. */}
            <NotEvaluated />

            {pending.length > 0 && (
              <section className="pending" data-testid="pending">
                <h3>
                  {pending.length} proposed change(s) awaiting approval — not in
                  force
                </h3>
                <p>
                  These are recorded but do not bind this portfolio. They are
                  shown here so a pending amendment is visible to whoever reads
                  the limits, not so it can be read as one.
                </p>
                <table className="grid proposed-here">
                  <thead>
                    <tr>
                      <th>Limit</th>
                      <th>Says</th>
                      <th>Set by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pending.map((hit) => {
                      const said = says(hit)
                      return (
                        <tr key={hit.id}>
                          <td>
                            <code>{hit.rule_key}</code>
                          </td>
                          <td>
                            <span className={said.numeric ? 'limit' : 'no-number'}>
                              {said.text}
                            </span>
                          </td>
                          <td>
                            <span className="pill pill-layer">{layerLabel(hit.layer)}</span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </section>
            )}

            <p className="as-of">
              As at {new Date(guidelines.as_of).toLocaleString()} · showing{' '}
              {rows.length} rule(s) across {guidelines.constraints.length}{' '}
              constraint(s)
            </p>

            {/* What the fund is, beside what binds it: its benchmark and base currency,
                facts rather than limits (decision 34). */}
            {(guidelines.facts ?? []).length > 0 && (
              <p className="fund-facts" data-testid="fund-facts">
                {guidelines.facts!.map((f) => (
                  <span key={f.name}>
                    {f.label}: <strong>{f.value}</strong>
                  </span>
                ))}
              </p>
            )}

            {undetermined && (
              <p className="undetermined" role="note" data-testid="undetermined">
                Some of these constrain the same thing in different terms — against
                a benchmark and in absolute terms, say — so which binds depends on
                the holdings. Both are shown and marked; your OMS settles it at the
                trade, and the <EvaluatorName strong={false} /> against a holdings
                snapshot.
                <AddOnFootnote />
              </p>
            )}

            {/* The three views sit on the table they act on, as tabs, with
                their counts. In a filter bar above the page they read as a
                fourth control beside the fund picker and the reader has to work
                out which of the two the table is obeying. */}
            <div className="tabstrip" role="tablist" aria-label="Which rules">
              {(
                [
                  ['all', 'All rules'],
                  ['inherited', 'Inherited only'],
                  ['own', 'This portfolio only'],
                ] as const
              ).map(([option, label]) => (
                <button
                  key={option}
                  role="tab"
                  aria-selected={filter === option}
                  onClick={() => setFilter(option)}
                >
                  {label}
                  <span className="count">{counts[option]}</span>
                </button>
              ))}
            </div>

            {rows.length === 0 ? (
              <p className="empty">
                No rules recorded for this portfolio under this filter. That is
                the absence of a <em>record</em>, not a statement that the
                mandate is unconstrained.
              </p>
            ) : (
              <table className="grid limits has-menus">
                <thead>
                  <tr>
                    <SortHeader
                      label="Constrains"
                      sortKey="subject"
                      sort={sort}
                      onSort={onSort}
                    />
                    <SortHeader label="Limit" sortKey="limit" sort={sort} onSort={onSort} />
                    <th>Says</th>
                    <SortHeader label="Set by" sortKey="layer" sort={sort} onSort={onSort} />
                    <SortHeader label="From" sortKey="origin" sort={sort} onSort={onSort} />
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const said = says(row.rule)
                    const showing = open === row.rule.id
                    return (
                      <Fragment key={row.rule.id}>
                        <tr data-testid={`rule-${row.standing}`}>
                          <td>
                            {subjectOf(row)}
                            <span className="subject">
                              {subjectText(row.rule.subject)}
                            </span>
                          </td>
                          <td>
                            <code>{row.rule.rule_key}</code>
                          </td>
                          <td>
                            <span className={said.numeric ? 'limit' : 'no-number'}>
                              {said.text}
                            </span>
                            {/* Only where the word means something. On the
                                ordinary fund every constraint holds one rule,
                                and `sole` down forty rows is a column that has
                                told the reader nothing. */}
                            {/* Terse, because it repeats down every row of a
                                competing group and the note above the table
                                carries the argument. The title has the rest,
                                for the reader who wants it on the row. */}
                            {row.competing && (
                              <span className={`standing standing-${row.standing}`}>
                                {row.standing === 'undetermined' ? (
                                  <>
                                    not ranked
                                    <Explain term="not ranked">
                                      {' '}— another rule constrains the same thing in
                                      different terms, and which is tighter depends
                                      on the holdings. Both are shown side by side;
                                      your OMS, or the{' '}
                                      <EvaluatorName strong={false} /> pack,
                                      settles it with holdings in hand.
                                      <AddOnFootnote />
                                    </Explain>
                                  </>
                                ) : row.standing === 'binding' ? (
                                  <>
                                    binds
                                    <Explain term="binds">
                                      {' '}— more than one rule constrains this, and
                                      this is the tightest of them, so this is the
                                      one that applies. Breadth does not decide it:
                                      a firm-wide policy tighter than the mandate
                                      binds, and so does a mandate tighter than the
                                      policy.
                                    </Explain>
                                  </>
                                ) : (
                                  <>
                                    overridden
                                    <Explain term="overridden">
                                      {' '}— another rule constrains the same thing
                                      more tightly, so that one applies instead.
                                      This rule is still recorded and still in
                                      force; it is simply not the binding one.
                                    </Explain>
                                  </>
                                )}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="pill pill-layer">
                              {layerLabel(row.rule.layer)}
                            </span>
                          </td>
                          <td>
                            <Origin rule={row.rule} />
                            <Cited rule={row.rule} />
                          </td>
                          <td className="actions">
                            {/* Launch gate 6, reached from where the limit is
                                read: "what was this before" is asked while
                                looking at the number, and a history two
                                navigations away is one nobody opens. */}
                            <RowMenu
                              label={`More about ${row.rule.rule_key}`}
                              testId="rule-menu"
                              items={[
                                {
                                  label: showing ? 'Hide history' : 'History',
                                  onSelect: () => setOpen(showing ? null : row.rule.id),
                                },
                                ...(canRetire
                                  ? [
                                      {
                                        label: 'Propose to retire',
                                        disabled: row.rule.inherited,
                                        why: 'It comes from a group: detach it on Groups',
                                        onSelect: () => {
                                          setNotice(null)
                                          setRetiring(row.rule.id)
                                        },
                                      },
                                    ]
                                  : []),
                              ]}
                            />
                          </td>
                        </tr>
                        {showing && (
                          <tr className="history-row">
                            <td colSpan={6}>
                              <RuleHistory ruleId={row.rule.id} />
                            </td>
                          </tr>
                        )}
                        {retiring === row.rule.id && (
                          <tr className="history-row">
                            <td colSpan={6}>
                              <RetireLimit
                                ruleId={row.rule.id}
                                what={row.rule.rule_key}
                                onDone={(message) => {
                                  setRetiring(null)
                                  setNotice(message)
                                }}
                                onCancel={() => setRetiring(null)}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            )}
          </section>
        )
      )}
    </div>
  )
}
